import { Badge, Button, Checkbox, FormLayout, Layout, Page, LegacyStack, TextField, Text } from "@shopify/polaris";
import { useHistory } from 'react-router-dom';
import CustomerCreditCard from '../customers/CustomerCreditCard';
import {useCallback, useEffect, useState} from 'react';
import Api from '../../helpers/api';
import SetLocationAccount from './SetAccount';
import {CountrySelector, StateSelector} from "../Selectors";

export default function ({ customerId, setLocation, location, handleSubmit, errors, loading, setLoading, transferCredit = () => {} }) {
    const history = useHistory();
    const [customer, setCustomer] = useState(null);
    const [showSetAccount, setShowSetAccount] = useState(false);
    const [customerBilling, setCustomerBilling] = useState({
        company: '',
        address1: '',
        address2: '',
        city: '',
        zip_code: '',
        state: '',
        country: '',
        type: '',
    });

    useEffect(() => {
        if (! customerId) {
            return;
        }
        setLoading(true);

        Api
            .getCustomer(customerId)
            .then( response => {
                setCustomer(response.data.data);

                setCustomerBilling(response.data.data.default_location?.billing ?? location.billing ?? {
                    company: '',
                    address1: '',
                    address2: '',
                    city: '',
                    zip_code: '',
                    state: '',
                    country: '',
                    type: '',
                });
                if (!location.id) {
                    setLocation(location);
                }
            })
            .finally(() => setLoading(false));
    }, [customerId]);

    const handleChange = useCallback((key, value) => {
        if ( typeof value === 'boolean') {
            value = (value) ? 1 : 0;
        }

        const payload = {
            [key]: value,
        };

        setLocation({
            ...location,
            ...payload,
        });
    }, [location]);

    const updateAddress = useCallback((type, key, value) => {
        setLocation({
            ...location,
            [type]: {
                ...location[type],
                [key]: value,
            },
        });
    }, [location]);

    const onLocationCardUpdate = (data) => {
        // If edit location - apply location; if not apply only credit_card_id
        setLocation(location.id
            ? data.location
            : {
                ...location,
                credit_card_id: data.card?.id,
                card: data.card,
            });
    }

    const secondaryActions = [
        (location.id && ! location.shopify_customer_id) && {
            content: 'Give unique customer account',
            onAction: () => setShowSetAccount(true),
        },
        location.id && {
            content: "Place Order",
            onAction: () => {
                history.push(`/orders/${customerId}?location_id=${location.id}`)
            }
        },
    ].filter(Boolean);

    const titleMetadata = location.shopify_customer_id
        ? (<Badge>Shopify Customer</Badge>)
        : null;

    const billingCheckboxChange = useCallback((newCheck) => {
        setLocation({
            ...location,
            billing: newCheck ?  customerBilling : null,
        });
    }, [location, customerBilling]);

    return (
        <Page
            title="Location"
            titleMetadata={titleMetadata}
            primaryAction={{
                content: 'Save',
                onAction: handleSubmit
            }}
            backAction={{ content: "Customer", onAction: () => {history.goBack()} }}
            secondaryActions={secondaryActions}
        >
            <Layout>
                <SetLocationAccount location={location}
                                    active={showSetAccount}
                                    onClose={() => setShowSetAccount(false)}
                                    setLocation={setLocation} />
                <Layout.Section>
                    <FormLayout>
                        <CustomerCreditCard
                            customer={customer}
                            onUpdate={onLocationCardUpdate}
                            type="location"
                            card={location.card}
                            locationId={location.id}
                        />

                        <FormLayout.Group>
                            <LegacyStack vertical>
                                <Checkbox
                                    checked={location.credit_enabled}
                                    onChange={(value) => {handleChange('credit_enabled', value)}}
                                    label="Credit Enabled"
                                    id="credit_enabled"
                                    error={errors['credit_enabled']}
                                    helpText={location.credit_expires_at ? `Credit expires at ${location.credit_expires_at}` : null}
                                    disabled={loading}
                                />
                                { location && location.credit_expires_at && (
                                    <Button onClick={transferCredit} loading={loading}>Transfer Credit to Customer</Button>
                                )}
                            </LegacyStack>

                            <TextField
                                value={(location.credit_enabled ? location.credit_balance : 0) + ''}
                                onChange={(value) => {handleChange('credit_balance', value)}}
                                disabled={!location.credit_enabled || loading}
                                label="Credit Balance"
                                type="number"
                                id="credit_balance"
                                error={errors['credit_balance']}
                            />
                        </FormLayout.Group>

                        <TextField disabled={loading} label="Phone Number" value={location.phone} onChange={(value) => {handleChange('phone', value)}} error={errors.phone}/>
                        {location.shopify_customer_id && <TextField disabled={loading} label="E-mail" value={location.shopify_customer_email} onChange={(value) => {handleChange('shopify_customer_email', value)}} error={errors.shopify_customer_email}/>}

                        <hr />
                        <Text variant="headingMd" as="h2">Addresses</Text>
                        <FormLayout.Group>
                            <LegacyStack vertical>
                            </LegacyStack>
                            <LegacyStack vertical>
                                <Checkbox disabled={loading} checked={location.billing} onChange={billingCheckboxChange} label={"Add billing address"} />
                            </LegacyStack>
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <LegacyStack vertical>
                                <Text variant="headingSm" as="h3">Shipping Address</Text>
                                <TextField disabled={loading} label="Company" value={location.shipping?.company} onChange={(value) => {updateAddress('shipping', 'company', value)}} error={errors['shipping.company']}/>
                                <TextField disabled={loading} label="Address 1" value={location.shipping?.address1} onChange={(value) => {updateAddress('shipping', 'address1', value)}} error={errors['shipping.address1']}/>
                                <TextField disabled={loading} label="Address 2" value={location.shipping?.address2} onChange={(value) => {updateAddress('shipping', 'address2', value)}} error={errors['shipping.address2']}/>
                                <TextField disabled={loading} label="City" value={location.shipping?.city} onChange={(value) => {updateAddress('shipping', 'city', value)}} error={errors['shipping.city']}/>
                                <TextField disabled={loading} label="Zip Code" value={location.shipping?.zip_code} onChange={(value) => {updateAddress('shipping', 'zip_code', value)}} error={errors['shipping.zip_code']}/>
                                <CountrySelector disabled={loading} value={location.shipping?.country} onChange={(value) => {updateAddress('shipping', 'country', value)}} error={errors['shipping.country']}/>
                                <StateSelector disabled={loading} value={location.shipping?.state} onChange={(value) => {updateAddress('shipping', 'state', value)}} error={errors['shipping.state']} selectedCountry={location.shipping?.country} />
                            </LegacyStack>
                            <LegacyStack vertical>
                                { location.billing && (<LegacyStack vertical>
                                    <Text variant="headingSm" as="h3">Billing Address</Text>
                                    <TextField disabled={loading} label="Company" value={location?.billing?.company} onChange={(value) => {updateAddress('billing', 'company', value)}} error={errors['billing.company']}/>
                                    <TextField disabled={loading} label="Address 1" value={location?.billing?.address1} onChange={(value) => {updateAddress('billing', 'address1', value)}} error={errors['billing.address1']}/>
                                    <TextField disabled={loading} label="Address 2" value={location?.billing?.address2} onChange={(value) => {updateAddress('billing', 'address2', value)}} error={errors['billing.address2']}/>
                                    <TextField disabled={loading} label="City" value={location?.billing?.city} onChange={(value) => {updateAddress('billing', 'city', value)}} error={errors['billing.city']}/>
                                    <TextField disabled={loading} label="Zip Code" value={location?.billing?.zip_code} onChange={(value) => {updateAddress('billing', 'zip_code', value)}} error={errors['billing.zip_code']}/>
                                    <CountrySelector disabled={loading} value={location.billing?.country} onChange={(value) => {updateAddress('billing', 'country', value)}} error={errors['billing.country']}/>
                                    <StateSelector disabled={loading} value={location.billing?.state} onChange={(value) => {updateAddress('billing', 'state', value)}} error={errors['billing.state']} selectedCountry={location.billing?.country} />
                                </LegacyStack>)}
                            </LegacyStack>
                        </FormLayout.Group>

                        <hr />

                        <FormLayout.Group>
                            <TextField disabled={loading} label="Notes" value={location.notes} onChange={(value) => {handleChange('notes', value)}} type="text" id="notes" error={errors.notes} multiline={4} />
                        </FormLayout.Group>

                        <Checkbox disabled={loading} label="Set as Default Location" checked={location.default} onChange={(value) => {handleChange('default', value)}} />

                        <Button loading={loading} onClick={handleSubmit}  variant="primary">Save</Button>
                    </FormLayout>
                </Layout.Section>
            </Layout>
        </Page>
    );
}
